import React, { useContext, useState } from "react";
import { LanguageContext } from "../../../../../Context";
import useDBO from "../../../hooks/useDBO";
import mut from "../../../hooks/mut";
import Lang from "../../../components/Lang";
import {
  CalendarClock,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  Sparkles,
  User,
  ArrowRight,
  ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";


/**
 * Pending Leave Approvals home widget.
 * Shows pending leave requests with quick approve/decline buttons.
 * Only visible to Manager, Admin, or Owner roles.
 * Uses the standard home-widget pattern matching Messages.jsx.
 *
 * @author Forge
 * @date 2026-04-08
 * @version 1.0
 */
export default function PendingLeaveApprovals({ onDataLoaded }) {
  const { l, language } = useContext(LanguageContext);
  const navigate = useNavigate();

  const leaveRequests = useDBO("/leave-requests/pending", 30);
  const [actioningId, setActioningId] = useState(null);
  const [declineExpanded, setDeclineExpanded] = useState(null);
  const [declineReasons, setDeclineReasons] = useState({});

  const pending = leaveRequests.get || [];

  React.useEffect(() => {
    if (leaveRequests.get && onDataLoaded) {
      onDataLoaded(pending.length);
    }
  }, [leaveRequests.get, onDataLoaded, pending.length]);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString(
      language === 0 ? "en-US" : "vi-VN",
      { month: "short", day: "numeric" }
    );

  const handleApprove = async (id) => {
    setActioningId(id);
    try {
      const res = await mut("put", `/leave-requests/${id}/approve`);
      if (res) leaveRequests.remove(id);
    } finally {
      setActioningId(null);
    }
  };

  const handleDecline = async (id) => {
    const reason = declineReasons[id];
    if (!reason || !reason.trim()) return;
    setActioningId(id);
    try {
      const res = await mut("put", `/leave-requests/${id}/decline`, {
        declinedReason: reason.trim(),
      });
      if (res) leaveRequests.remove(id);
    } finally {
      setActioningId(null);
      setDeclineExpanded(null);
    }
  };

  return (
    <div className="home-widget flex flex-col h-full bg-white border border-slate-200 rounded-[24px] shadow-sm overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
        <h3 className="font-heading text-[1.25rem] font-semibold text-slate-900 flex items-center gap-2">
          <CalendarClock
            size={22}
            className="text-teal-800"
          />
          <Lang w="pendingLeave" />
        </h3>
        <div className="flex items-center gap-2">
          {pending.length > 0 && (
            <span className="bg-amber-100 text-amber-700 px-2.5 py-0.5 rounded-full text-xs font-bold">
              {pending.length}
            </span>
          )}
          <button
            className="bg-white hover:bg-slate-50 text-slate-700 font-bold py-1.5 px-4 rounded-xl text-xs border border-slate-200 shadow-sm transition-all active:scale-95 cursor-pointer flex items-center gap-2"
            onClick={() => navigate("/manage/leave-approvals")}
          >
            <Lang w="viewAll" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 bg-white max-h-[500px]">
        {leaveRequests.loading && !leaveRequests.get && (
          <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 py-10">
            <Loader2 size={28} className="animate-spin mb-3 opacity-30" />
            <p className="text-sm">
              <Lang w="loadingLeave" />
            </p>
          </div>
        )}

        {leaveRequests.get && pending.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 py-10">
            <Sparkles size={36} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">
              <Lang w="noPendingLeave" />
            </p>
          </div>
        )}

        {leaveRequests.get && pending.length > 0 && (
          <div className="flex flex-col gap-4">
            {pending.map((request) => {
              const isActioning = actioningId === request._id;
              const isDeclineOpen = declineExpanded === request._id;
              const staffName =
                request.user?.name || request.user || "Unknown";

              return (
                <div
                  key={request._id}
                  className="bg-amber-50/50 border border-amber-100 rounded-2xl p-4 transition-all duration-200 hover:shadow-sm"
                >
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <User size={14} className="text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-slate-900 truncate">
                        {staffName}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Calendar size={10} />
                        <span>
                          {formatDate(request.startDate)}{" "}
                          <ArrowRight
                            size={10}
                            className="inline mx-0.5"
                          />{" "}
                          {formatDate(request.endDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 mb-3 line-clamp-2">
                    {request.reason}
                  </p>

                  {isDeclineOpen && (
                    <div className="mb-3 flex gap-1.5">
                      <input
                        type="text"
                        className="profile-input flex-1 text-xs py-1.5"
                        placeholder={l["enterDeclineReason"][language]}
                        value={declineReasons[request._id] || ""}
                        onChange={(e) =>
                          setDeclineReasons({
                            ...declineReasons,
                            [request._id]: e.target.value,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            handleDecline(request._id);
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => handleDecline(request._id)}
                        disabled={
                          !declineReasons[request._id]?.trim() ||
                          isActioning
                        }
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg font-bold text-xs hover:bg-red-700 transition-all disabled:opacity-50"
                      >
                        {isActioning ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <XCircle size={10} />
                        )}
                        <Lang w="submit" />
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleApprove(request._id)}
                      disabled={isActioning}
                      className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg font-bold text-xs hover:bg-emerald-700 transition-all disabled:opacity-50"
                    >
                      {isActioning ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        <CheckCircle size={10} />
                      )}
                      <Lang w="approveLeave" />
                    </button>
                    {!isDeclineOpen && (
                      <button
                        onClick={() => setDeclineExpanded(request._id)}
                        disabled={isActioning}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg font-bold text-xs hover:bg-red-100 transition-all disabled:opacity-50"
                      >
                        <XCircle size={10} />
                        <Lang w="declineLeave" />
                      </button>
                    )}
                    {isDeclineOpen && (
                      <button
                        onClick={() => setDeclineExpanded(null)}
                        className="px-2 py-1.5 bg-slate-100 text-slate-500 rounded-lg text-xs hover:bg-slate-200 transition-all"
                      >
                        <ChevronUp size={10} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
